import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { storyChapters } from "../mock/mock";
import { Reveal, EASE } from "../components/Reveal";

const Story = () => {
  const sectionRef = useRef(null);
  const [current, setCurrent] = useState(1);
  const [inStory, setInStory] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const sec = sectionRef.current;
      if (!sec) return;
      const mid = window.innerHeight * 0.5;
      const r = sec.getBoundingClientRect();
      setInStory(r.top < mid && r.bottom > mid);
      let active = 1;
      sec.querySelectorAll("[data-chapter]").forEach((el) => {
        if (el.getBoundingClientRect().top <= mid) active = Number(el.dataset.chapter);
      });
      setCurrent(active);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const jump = (n) => {
    const el = document.getElementById(`chapter-${String(n).padStart(2, "0")}`);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { duration: 1.2, offset: -40 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={sectionRef}
      id="story"
      data-theme="dark"
      className="sec-dark relative px-6 md:px-12 py-28 md:py-40"
      data-testid="story-section"
    >
      <div className="max-w-[1440px] mx-auto">
        <Reveal>
          <div className="eyebrow">01 · The Story</div>
        </Reveal>

        {storyChapters.map((ch, i) => (
          <div
            key={ch.n}
            id={`chapter-${ch.n}`}
            data-chapter={i + 1}
            className="grid grid-cols-12 gap-x-10 md:gap-x-16 pt-24 md:pt-36"
            data-testid={`chapter-${ch.n}`}
          >
            <div className="col-span-12 lg:col-span-4">
              <div className="lg:sticky lg:top-28">
                <Reveal>
                  <div className="ghost-num">{ch.n}</div>
                </Reveal>
                <Reveal delay={0.08}>
                  <div className="mt-5 text-[11px] font-semibold tracking-[0.26em] uppercase t-dim">
                    {ch.setting}
                  </div>
                </Reveal>
                <Reveal delay={0.16}>
                  <h3 className="font-serif-italic text-[24px] md:text-[30px] leading-[1.3] t-fg mt-6 max-w-[24ch]">
                    &ldquo;{ch.title}&rdquo;
                  </h3>
                </Reveal>
              </div>
            </div>
            <div className="col-span-12 lg:col-span-7 lg:col-start-6 mt-10 lg:mt-2">
              <Reveal delay={0.12}>
                <div className="prose-memoir drop">
                  {ch.body.map((p, idx) => (
                    <p key={idx}>{p}</p>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        ))}
      </div>

      {/* Quiet chapter marker — visible only while reading the story */}
      <AnimatePresence>
        {inStory && (
          <motion.div
            initial={{ opacity: 0, x: 14 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 14 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="fixed right-5 md:right-9 top-1/2 -translate-y-1/2 z-40 hidden md:flex flex-col items-end gap-6"
            data-testid="chapter-marker"
          >
            <div className="[writing-mode:vertical-rl] text-[10px] font-semibold tracking-[0.3em] uppercase t-dim select-none">
              Chapter {String(current).padStart(2, "0")} / 08
            </div>
            <div className="flex flex-col items-end gap-2.5">
              {storyChapters.map((ch, i) => (
                <button
                  key={ch.n}
                  onClick={() => jump(i + 1)}
                  className="group py-1"
                  aria-label={`Go to chapter ${i + 1}`}
                  data-testid={`chapter-tick-${ch.n}`}
                >
                  <span
                    className={`block h-px transition-all duration-500 ${
                      current === i + 1
                        ? "w-8 bg-terra"
                        : "w-4 bg-[rgba(245,245,247,0.25)] group-hover:bg-[rgba(245,245,247,0.6)]"
                    }`}
                  />
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Story;
